import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress } from '@mui/material'
import { DataGrid, GridPaginationModel } from '@mui/x-data-grid'
import { usePagedCommandHistory } from '@src/api/dev-tools-history-api'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCommandHistoryColumns } from './CommandHistoryColumns'

interface CommandHistoryDialogProps {
  open: boolean
  onClose: () => void
  commandType: DevToolsCommandType
  commandTitle: string
}

const DEFAULT_PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [10, 25, 50]

const CommandHistoryDialog = ({ open, onClose, commandType, commandTitle }: CommandHistoryDialogProps) => {
  const { t } = useTranslation()
  const columns = useCommandHistoryColumns({ includeErrorMessage: true })

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const { data, isLoading, isFetching, isError } = usePagedCommandHistory(
    commandType,
    paginationModel.page * paginationModel.pageSize,
    paginationModel.pageSize,
  )

  const rows = data ?? []
  const isLastPage = rows.length < paginationModel.pageSize
  const estimatedRowCount = paginationModel.page * paginationModel.pageSize + rows.length + (isLastPage ? 0 : 1)

  const handlePaginationChange = (model: GridPaginationModel) => {
    setPaginationModel(model)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('admin.devTools.history.dialogTitle', { commandTitle })}</DialogTitle>
      {isFetching && <LinearProgress />}
      <DialogContent dividers>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('admin.devTools.history.loadError')}
          </Alert>
        )}
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          rowCount={estimatedRowCount}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disableColumnFilter
          disableColumnSelector
          disableRowSelectionOnClick
          density="compact"
          sx={{ minHeight: 400 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('admin.devTools.history.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default CommandHistoryDialog
